import type { ReactNode } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { cn } from '../lib/cn'

/* ── BarList ──────────────────────────────────────────────────────────────
   A ranked list where the bar is the row, not a separate chart. Reads as a
   table on a phone and as a chart on a desk, which is why both consoles use it
   for "where the money goes" and "what people ask about".
   ────────────────────────────────────────────────────────────────────────── */

export interface BarListItem {
  id: string
  label: ReactNode
  /** The number that sizes the bar. */
  value: number
  /** What is printed at the end of the row — money goes through `<Money>`. */
  display: ReactNode
  tone?: 'accent' | 'success' | 'warn' | 'danger' | 'muted'
}

export function BarList({
  items,
  className,
  max,
}: {
  items: BarListItem[]
  className?: string
  max?: number
}) {
  const ceiling = max ?? Math.max(1, ...items.map((item) => item.value))
  const fills = {
    accent: 'bg-accent',
    success: 'bg-success',
    warn: 'bg-warn',
    danger: 'bg-danger',
    muted: 'bg-muted',
  } as const

  return (
    <ul className={cn('flex flex-col gap-3', className)}>
      {items.map((item) => (
        <li key={item.id}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-[13px] text-sub">{item.label}</span>
            <span className="shrink-0 font-display text-[13px] font-bold tnum text-dark">
              {item.display}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-[4px] bg-muted/20">
            <div
              className={cn('h-full rounded-[4px]', fills[item.tone ?? 'accent'])}
              style={{ width: `${Math.min(100, (item.value / ceiling) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

/* ── SparkBar — the deck's `.spark`: six columns and their labels ─────────── */

export function SparkBar({
  data,
  className,
  ariaLabel,
}: {
  data: { label: string; value: number }[]
  className?: string
  ariaLabel: string
}) {
  const max = Math.max(1, ...data.map((point) => point.value))

  return (
    <figure className={cn('mt-2', className)} aria-label={ariaLabel}>
      <div className="flex h-[88px] items-end gap-[7px]">
        {data.map((point, index) => {
          const last = index === data.length - 1
          return (
            <div
              key={point.label}
              title={`${point.label}: ${point.value}`}
              className={cn(
                'flex-1 rounded-t-[5px] border border-b-0',
                last
                  ? 'border-accent bg-accent'
                  : 'border-accent-glass-border bg-accent-glass',
              )}
              style={{ height: `${Math.max(8, (point.value / max) * 100)}%` }}
            />
          )
        })}
      </div>
      <div className="mt-[7px] flex gap-[7px]">
        {data.map((point) => (
          <span key={point.label} className="flex-1 text-center text-[10px] font-semibold text-muted">
            {point.label}
          </span>
        ))}
      </div>
    </figure>
  )
}

/* ── LineChart ────────────────────────────────────────────────────────────
   Recharts, themed from the token custom properties rather than hex props, so
   it retints with the console it is rendered in.
   ────────────────────────────────────────────────────────────────────────── */

export function LineChart({
  data,
  xKey,
  yKey,
  height = 200,
  formatY,
  ariaLabel,
}: {
  data: Record<string, string | number>[]
  xKey: string
  yKey: string
  height?: number
  formatY?: (value: number) => string
  ariaLabel: string
}) {
  const tick = { fontSize: 11, fill: 'var(--color-muted)' }

  return (
    <div style={{ height }} aria-label={ariaLabel} role="img">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--color-muted)" strokeOpacity={0.18} vertical={false} />
          <XAxis dataKey={xKey} tick={tick} axisLine={false} tickLine={false} />
          <YAxis
            tick={tick}
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={formatY ? (value: number) => formatY(value) : undefined}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--color-glass-heavy-solid)',
              border: '1px solid var(--color-glass-border)',
              borderRadius: 12,
              fontSize: 12,
            }}
            formatter={formatY ? (value) => formatY(Number(value)) : undefined}
          />
          <Line
            type="monotone"
            dataKey={yKey}
            stroke="var(--color-accent)"
            strokeWidth={2.5}
            dot={false}
          />
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  )
}
